/**
 * 主题（外观）**中文文案唯一源** —— 依据原型 `08-家庭与设置_原型.html`（「外观」卡片）
 *
 * 依据：N 域 PRD **§5.3 N3**「主题 · 三态：跟随系统（默认）/ 浅色 / 深色；持久化到服务端」
 * （`AC-N3.1` / `AC-N3.2` / `AC-N3.3`）· 原型 `08-家庭与设置_原型.html` 的
 * `.card-head`（外观 / 双端同源异构）与 `.segmented`（浅色 / 深色 / 跟随系统）。
 *
 * 🔴 **选项顺序 = 原型顺序（浅色 → 深色 → 跟随系统）** —— 不是契约 `ThemeValues` 的顺序
 * （契约只声明取值集，展示顺序归本文件；两者混淆会让"改契约顺序"顺手改掉 UI）。
 *
 * 🔴 铁律 38：UI 文案里**不出现内部词**（`system` / `pref`）。
 */

import type { ThemeValue } from '@jiaying/shared';

/** 卡片标题（原型 `.card-head > h3`） */
export const SECTION_TITLE = '外观';

/** 卡片副标题（原型 `.card-head > .sub` 原文） */
export const SECTION_NOTE = '双端同源异构：色 / 字 / 距 同源，布局不同源';

/** 「主题」行标题与说明（原型 `.row-title` / `.row-desc` 原文） */
export const THEME_TITLE = '主题';
export const THEME_DESC = '浅色 / 深色 / 跟随系统';

/** 作用域说明（`DR-020` 层级铁律：用户级项必须显式标注） */
export const THEME_SCOPE_NOTE = '这是你自己的设置，家人不受影响；换设备登录后保持。';

/** 保存反馈 */
export const SAVED_LABEL = '已保存';
export const SAVE_FAILED_PREFIX = '保存失败：';

/** 品牌色行（原型原文：**仅展示不可选**） */
export const BRAND_TITLE = '品牌色';
export const BRAND_DESC =
  '祖母绿体系（延续 v1，降低迁移认知成本）· 不可配置（token 单源；改色必过对比度实算）';
export const BRAND_READONLY_LABEL = '仅展示 · 不可选';

/** 一个可选项 —— `id` 直接就是契约取值（端侧**不建**映射表） */
export interface ThemeOption {
  id: ThemeValue;
  label: string;
}

/** 🔴 渲染顺序 = 原型 `segmented` 顺序（浅色 / 深色 / 跟随系统） */
export const THEME_OPTIONS: readonly ThemeOption[] = [
  { id: 'light', label: '浅色' },
  { id: 'dark', label: '深色' },
  { id: 'system', label: '跟随系统' },
];

/** 三态 → 中文（用于「当前：…」这类回显；找不到时**回退到原值**而不是空串） */
export function themeLabelOf(pref: ThemeValue): string {
  return THEME_OPTIONS.find((o) => o.id === pref)?.label ?? pref;
}

/**
 * 「当前实际显示」的一句说明。
 *
 * 🔴 **必须把"跟随系统"的解析结果说出来**：选了「跟随系统」的用户看到的是深色，
 * 如果不告诉他"这是按系统来的"，他会以为主题设置坏了（`AC-N3.1` 的可用性半边）。
 *
 * @param pref 用户偏好三态
 * @param mode 解析后的明/暗（`resolveThemeMode()` 的结果）
 */
export function themeEffectiveNote(pref: ThemeValue, mode: 'light' | 'dark'): string {
  const shown = mode === 'dark' ? '深色' : '浅色';
  return pref === 'system' ? `当前按你的系统显示：${shown}` : `当前：${shown}`;
}
